<template>
  <div class="download">
    <div class="download-banner">
      <div class="flex-none download-banner-image">
        <slot class="w-full h-full cover-image" name="image"></slot>
      </div>

      <div class="info flex-1">
        <div class="title">{{ title }}</div>
        <slot name="description"></slot>
        <button
          @click="openDownloadModal"
          class="btn btn-secondary font-medium link input-tall sm:w-full"
        >
          {{ ctaText || 'Download' }}
        </button>
      </div>
    </div>

    <zb-modal ref="firstModal">
      <h3 slot="title" class="download-title mb-6">Enter your details to get access</h3>

      <template v-slot:default="{ modal }">
        <div>
          <validation-observer v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(onSubmit)">
              <div class="form-group">
                <validation-provider
                  name="First name"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <label class="label">First name</label>
                  <input
                    v-model="firstname"
                    type="text"
                    class="form-control fs-16"
                    :class="{ 'input--error': Boolean(errors[0]) }"
                  >
                  <span class="error">{{ errors[0] }}</span>
                </validation-provider>
              </div>
              <div class="form-group">
                <validation-provider
                  name="Last name"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <label class="fs-14">Last name</label>
                  <input
                    v-model="lastname"
                    class="form-control fs-16"
                    :class="{ 'input--error': Boolean(errors[0]) }"
                  >
                  <span class="error">{{ errors[0] }}</span>
                </validation-provider>
              </div>
              <div class="form-group">
                <validation-provider
                  name="Email"
                  rules="required|email"
                  v-slot="{ errors }"
                >
                  <label class="fs-14">Email</label>
                  <input
                    v-model.lazy="email"
                    class="form-control fs-16"
                    :class="{ 'input--error': Boolean(errors[0]) }"
                  >
                  <span class="error">{{ errors[0] }}</span>
                </validation-provider>
              </div>
              <p class="fs-14">
                By submitting your email address, you agree that, from time to time, ZuBlu may also send you news, insights and exciting dive travel opportunities that we think you might be interested in. We never share your details with third parties and you can unsubscribe at any time.
              </p>

              <button class="btn btn-secondary font-medium input-tall sm:w-full mr-2 sm:mr-0 sm:mb-2">
                Download
              </button>
              <button
                type="button"
                class="btn btn-outlined btn-tertiary font-medium input-tall sm:w-full mr-2 sm:mr-0 sm:mb-2"
                @click="modal.hide"
              >
                Cancel
              </button>
            </form>
          </validation-observer>
        </div>
      </template>
    </zb-modal>

    <zb-modal ref="thank" class="zb-small-modal">
      <h3 slot="title" class="download-title mb-4">You’re all set!</h3>

      <template v-slot:default="{ modal }">
        <div v-if="iOS()">
          <p style="margin-bottom: 32px">
            Your guide is ready. You have also been successfully added to our community, so you'll now be first to get ZuBlu updates and exciting dive travel offers, straight to your inbox.
          </p>
          <a :href="link" target='_blank'>
            <button
              type="button"
              class="btn btn-secondary input-tall sm:w-full"
              @click="modal.hide"
            >
              Open Guide
            </button>
          </a>
        </div>
        <div v-else>
          <p style="margin-bottom: 32px" v-if="isPopupBlocked === false">
            Your guide has opened in a new window. You have also been successfully added to our community, so you'll now be first to get ZuBlu updates and exciting dive travel offers, straight to your inbox.
          </p>
          <div style="margin-bottom: 32px" v-else>
            Your guide is ready. You have also been successfully added to our community, so you'll now be first to get ZuBlu updates and exciting dive travel offers, straight to your inbox.

            <p style="margin-top: 16px">
              You can view the guide by clicking on the link below.
            </p>
          </div>

          <a class="download-link" :href="link" target="_blank">
            <button
              type="button"
              class="btn btn-secondary input-tall sm:w-full"
              @click="modal.hide"
            >
              Open Guide
            </button>
          </a>
        </div>
      </template>
    </zb-modal>

  </div>
</template>

<script>
import {
  ValidationProvider,
  extend,
  ValidationObserver,
  setInteractionMode
} from 'vee-validate'
import ZbModal from '../../components/core/ZbModal'
import { required, email } from 'vee-validate/dist/rules'

setInteractionMode('eager')
extend('email', email)
extend('required', {
  ...required,
  message: field => `Please enter your ${field}`
})

export default {
  name: "DownloadGuide",
  components: {
    ValidationProvider,
    ValidationObserver,
    ZbModal
  },
  props: [
    'title',
    'link',
    'url',
    'link',
    'guide',
    'ctaText'
  ],
  data() {
    return {
      firstname: '',
      lastname: '',
      email: '',
      hasSubmitted: false,
      isPopupBlocked: false
    }
  },
  methods: {
    openDownloadModal() {
      this.$refs.firstModal.show()
    },
    onSubmit() {
      window.pushDataLayer("zublu_cta", {
        cta_type: "download",
        cta_value: this.link,
        cta_screen: window.location.pathname
      });

      $.ajax({
        method: 'POST',
        url: '/',
        data: {
          action: 'zuBlu/form/subscribeDownload',
          [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
          formData: {
            firstname: this.firstname,
            lastname: this.lastname,
            guide: {
              id: this.guide
            },
            email: this.email,
            url: this.url
          }
        },
        headers: {'Content-Type': 'application/x-www-form-urlencoded'}
      })
      .done(resp => {
        if (!this.iOS()) {
          const popup = window.open(this.link)

          try {
            popup.focus()
          }
          catch (e) {
            this.isPopupBlocked = true
          }
        }
        this.$refs.firstModal.hide()
        this.$refs.thank.show()
      })
      .fail(e => console.log(e))
    },
    iOS() {
      return navigator.userAgent && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    }
  },
}
</script>

<style lang="scss">
.download {
  .download-banner {
    display: flex;
    align-items: center;
  }

  .download-banner-image {
    width: 25%;
  }

  .zb-small-modal .zb-modal-close {
    display: none;
  }

  .info {
    padding-left: 40px;
  }
  .info .title {
    font-weight: 500;
    font-size: 24px;
    line-height: 28px;
    margin-bottom: 16px;
  }
  .info .description {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 24px;
  }

  .download-title {
    margin-top: 0px;
    font-weight: 500;
  }

  form {
    label {
      display: inline-block;
      text-transform: none;
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
      color: #222222;
      margin-bottom: 12px;
    }
    p {
      color: #727272;
      margin-bottom: 24px;
      font-size: 14px !important;
    }
    .form-group {
      margin-bottom: 24px;
    }
    .form-control {
      padding: 12px 16px;
      background: white;
      border: 1px solid #E0E0E0;
      border-radius: 4px;
      font-weight: normal;
      font-style: normal;
      height: 40px;
      display: block;
      width: 100%;

      &:focus {
        box-shadow: none;
      }
    }
    /* .btn-outline {
      border: 1px solid #222222;
      background: white;
      padding: 0 20px;
      border-radius: 4px;
    }
    .btn {
      font-weight: 500;
    } */
    span.error {
      color: #E12F22;
      padding-top: 5px;
      font-size: 12px;
      font-weight: 100;
    }
  }

  @media only screen and (max-width: 600px) {
    .modal-dialog {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
    }

    .modal-content {
      height: auto;
      min-height: 100%;
      border-radius: 0;
    }

    /* .btn {
      margin-bottom: 8px;
    } */

    .title, .description {
      text-align: center;
    }
  }
}

@media only screen and (max-width: 600px) {
  .download-banner {
    flex-direction: column;
  }

  .download .info {
    padding-left: 0px;
  }

  .download .download-banner-image {
    width: 50%;
    margin-bottom: 24px;
  }
}
</style>
